import type { DashboardCardType, DashboardLayoutZone } from "../../../../types/dashboardLayout"

import { dashboardCardRegistry } from "./dashboardCardRegistry"

/** Cards that cannot be swapped out of their slot in edit mode. */
const NON_REPLACEABLE_CARD_TYPES = new Set<DashboardCardType>(["tradeActivityCalendar"])

/** Cards that must stay on the layout (hide/remove disabled; may still be swappable). */
const NON_REMOVABLE_CARD_TYPES = new Set<DashboardCardType>([
  "tradeActivityCalendar",
  "portfolioGrowth",
  "winRateTrend",
])

export function canReplaceDashboardLayoutCard(cardType: DashboardCardType): boolean {
  return !NON_REPLACEABLE_CARD_TYPES.has(cardType)
}

export function canRemoveDashboardLayoutCard(
  cardType: DashboardCardType,
  zone: DashboardLayoutZone
): boolean {
  if (zone === "miniStats") return false
  if (NON_REMOVABLE_CARD_TYPES.has(cardType)) return false
  return dashboardCardRegistry[cardType].removable
}

export function isDashboardLayoutCardEditable(
  cardType: DashboardCardType,
  zone: DashboardLayoutZone
): boolean {
  return (
    canReplaceDashboardLayoutCard(cardType) || canRemoveDashboardLayoutCard(cardType, zone)
  )
}

/**
 * Legacy mini-stat (`portfolio` / "Portfolio Size") — superseded by `expectancy`.
 * Kept for render-only on saved layouts; never offered in the add/replace picker.
 */
const MINI_STAT_PICKER_EXCLUDED_CARD_TYPES = new Set<DashboardCardType>(["portfolio"])

/** At most one visible card per group (prevents duplicate metrics under different card types). */
const MINI_STAT_EXCLUSIVE_GROUPS: readonly DashboardCardType[][] = [["portfolio", "expectancy"]]

export function isDashboardMiniStatPickerCard(cardType: DashboardCardType): boolean {
  return !MINI_STAT_PICKER_EXCLUDED_CARD_TYPES.has(cardType)
}

export function isDashboardMiniStatDuplicateOfVisible(
  cardType: DashboardCardType,
  usedVisibleCardTypes: ReadonlySet<DashboardCardType>
): boolean {
  for (const group of MINI_STAT_EXCLUSIVE_GROUPS) {
    if (!group.includes(cardType)) continue
    return group.some((member) => member !== cardType && usedVisibleCardTypes.has(member))
  }
  return false
}

export function canAddDashboardMiniStatCard(
  cardType: DashboardCardType,
  visibleCardTypes: Iterable<DashboardCardType>
): boolean {
  if (!isDashboardMiniStatPickerCard(cardType)) return false
  const used = new Set(visibleCardTypes)
  if (used.has(cardType)) return false
  return !isDashboardMiniStatDuplicateOfVisible(cardType, used)
}
